/**
 * useDebounce Hook
 *
 * Debounces a value by delaying updates until the value has stopped changing
 * for the specified delay period. Perfect for search inputs.
 *
 * @example
 * const [query, setQuery] = useState('');
 * const debouncedQuery = useDebounce(query, 300);
 *
 * useEffect(() => {
 * // Only runs 300ms after user stops typing
 * performSearch(debouncedQuery);
 * }, [debouncedQuery]);
 */

import { useEffect, useState } from 'react';

export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set up the timeout
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clean up the timeout if value changes before delay expires
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
